import { Button, Skeleton, Stack, TabPanel, Text, useToast, VStack } from '@chakra-ui/react';

import { InputField } from 'src/components/InputField';
import { ProfileImage } from '@/components/ProfileImage';
import { formatDocument } from '@/utils/formatDocument';
import { formatPhoneNumber } from '@/utils';

import { SubmitHandler, useForm } from 'react-hook-form';
import { updateRegistrationDataFormSchema, type UpdateRegistrationDataFormData } from './schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useRef } from 'react';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { InputCpfCnpj, InputPhone } from '@/components/ControlledInput';
import { useAuthContext } from '@/contexts/AuthContext';
import {
  useGetDashboardUserByIdQuery,
  usePutDashboardUserAvatarMutation,
  usePutDashboardUserMutation,
} from '@/services/queries/dashboard-user';
import { PutDashboardUserRequest } from '@/services/request/dashboard-user';
import { useTranslation } from 'react-i18next';

export const ProfileRegistrationDataTabContent = () => {
  const { t } = useTranslation();
  const { authenticationInfo } = useAuthContext();
  const { data, isLoading, forceRefetch } = useGetDashboardUserByIdQuery(authenticationInfo?.uuid as string);

  const putDashboardUserAvatarMutation = usePutDashboardUserAvatarMutation();
  const putDashboardUserMutation = usePutDashboardUserMutation();

  const {
    control,
    handleSubmit,
    register,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<UpdateRegistrationDataFormData>({
    defaultValues: {
      document: data?.document,
      name: data?.name,
      phone: data?.phone_number,
    },
    resolver: zodResolver(updateRegistrationDataFormSchema),
  });

  const toast = useToast();

  const formRef = useRef<HTMLFormElement>(null);

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const renderFieldSkeletons = (fieldsQuantity: number) => {
    return new Array(fieldsQuantity).fill((_, index) => <Skeleton key={index} w='20%' h='small' />);
  };

  const updateProfileImage = (image: File) => {
    putDashboardUserAvatarMutation.mutate(
      { id: data?.uuid as string, payload: { image } },
      {
        onSuccess: () => {
          toast({
            status: 'success',
            title: t(
              'pages.profile.components.profile_registration_data_tab_content.profile_registration_data_tab_content_avatar_success_toast',
            ),
          });
          forceRefetch();
        },
        onError: (resp: any) => {
          toast({
            status: 'error',
            title:
              (resp && resp.message) ||
              t(
                'pages.profile.components.profile_registration_data_tab_content.profile_registration_data_tab_content_avatar_error_toast',
              ),
          });
        },
      },
    );
  };

  const onSubmit: SubmitHandler<UpdateRegistrationDataFormData> = async formData => {
    const payload: PutDashboardUserRequest = {
      name: formData.name,
      document: formData.document,
      phone_number: formData.phone,
    };

    putDashboardUserMutation.mutate(
      { id: data?.uuid as string, payload },
      {
        onSuccess: () => {
          toast({
            status: 'success',
            title: t(
              'pages.profile.components.profile_registration_data_tab_content.profile_registration_data_tab_content_update_success_toast',
            ),
          });
          forceRefetch();
        },
        onError: (resp: any) => {
          toast({
            status: 'error',
            title:
              (resp && resp.message) ||
              t(
                'pages.profile.components.profile_registration_data_tab_content.profile_registration_data_tab_content_update_error_toast',
              ),
          });
        },
      },
    );
  };

  useEffect(() => {
    const currentValues = getValues();

    if (isLoading || !data || currentValues?.document) return;

    setValue('document', formatDocument(data?.document || ''));
    setValue('name', data?.name);
    setValue('phone', formatPhoneNumber(data?.phone_number || ''));
  }, [data, data?.document, getValues, isLoading, setValue]);

  return (
    <TabPanel>
      <Text variant='tabContentTitle' mb='kilo'>
        {t('pages.profile.components.profile_registration_data_tab_content.profile_registration_data_tab_content_title')}
      </Text>

      <Stack flex={1}>
        <VStack ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} gap='kilo' align='start'>
          <ProfileImage
            src={data?.avatar_url}
            onImageChange={updateProfileImage}
            isLoading={putDashboardUserAvatarMutation.isLoading}
          />

          {isLoading && renderFieldSkeletons(4)}
          {!isLoading && (
            <>
              <InputCpfCnpj
                control={control}
                {...register('document')}
                name='document'
                label={t(
                  'pages.profile.components.profile_registration_data_tab_content.profile_registration_data_tab_content_document_label',
                )}
                rootProps={{ isInvalid: !!errors?.document, errorMessage: errors?.document?.message }}
              />
              <InputField
                label={t(
                  'pages.profile.components.profile_registration_data_tab_content.profile_registration_data_tab_content_name_label',
                )}
                rootProps={{ isInvalid: !!errors?.name, errorMessage: errors?.name?.message }}
                {...register('name')}
              />
              {/* email é a credencial de login (Firebase Auth) — não é editável */}
              <InputField
                label={t(
                  'pages.profile.components.profile_registration_data_tab_content.profile_registration_data_tab_content_email_label',
                )}
                type='email'
                value={data?.email ?? ''}
                isDisabled
              />
              <InputPhone
                label={t(
                  'pages.profile.components.profile_registration_data_tab_content.profile_registration_data_tab_content_phone_label',
                )}
                control={control}
                {...register('phone')}
                rootProps={{ isInvalid: !!errors?.phone, errorMessage: errors?.phone?.message }}
              />
            </>
          )}

          <Button onClick={handleConfirm}>
            {t(
              'pages.profile.components.profile_registration_data_tab_content.profile_registration_data_tab_content_save_button',
            )}
          </Button>
        </VStack>
      </Stack>

      <CenteredSpinner isLoading={putDashboardUserAvatarMutation.isLoading || isLoading || putDashboardUserMutation.isLoading} />
    </TabPanel>
  );
};
